import { ComponentType, FC } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { ErrorHOC } from '../ErrorHOC';

/**
 * Options for configuring the Editable Wrapper.
 */
interface EditableWrapperOptions {
  /** Indicates if the app is running as a Remote SPA */
  isRemoteSPA: boolean;
  /** Name of the component (used for logging and labeling) */
  componentName: string;
  /** AEM resource type for the component */
  resourceType: string;
  /** Indicates if the app is in authoring mode */
  isAuthorMode: boolean;
}

/**
 * Configuration object for the EditableComponent.
 */
interface EditableComponentConfig {
  /** Label to display when the component is empty */
  emptyLabel: string;
  /** Flag indicating whether the component is empty */
  isEmpty: boolean;
  /** AEM resource type */
  resourceType: string;
}

/**
 * Higher-order component (HOC) that wraps a given component with AEM EditableComponent
 * and error handling logic. It conditionally wraps the component based on whether the app
 * is running as a Remote SPA.
 *
 * @template P - Props type of the wrapped component
 * @param Component - The React component to wrap
 * @param options - Configuration options for the wrapper
 * @returns A new functional component with editable and error handling capabilities
 */
export const WithEditableWrapper = <P extends object>(
  Component: ComponentType<P>,
  { isRemoteSPA, componentName, resourceType, isAuthorMode }: EditableWrapperOptions
): FC<P> => {
  // Wrap the component with error handling HOC
  const ComponentWithHOC = ErrorHOC(Component, componentName);

  // Define the final wrapped component
  const WrappedComponent: FC<P> = (props: P) => {
    if (isRemoteSPA) {
      const editableComponentConfig: EditableComponentConfig = {
        emptyLabel: `${componentName}Component`,
        isEmpty: false,
        resourceType: resourceType,
      };

      return (
        // @ts-expect-error config for isEmpty is expected by EditableComponent
        <EditableComponent config={editableComponentConfig} {...{ ...props, isAuthorMode }}>
          <ComponentWithHOC {...{ ...props, isAuthorMode }} />
        </EditableComponent>
      );
    }

    return <ComponentWithHOC {...props} />;
  };

  return WrappedComponent;
};
